import { type FormEvent, useEffect, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Button, ErrorNote, Field, Input } from "@/components/ui";
import { useAuth } from "@/lib/auth";
import { AuthFrame, messageOf } from "./AuthFrame";

/**
 * Where the email link lands: `/confirm?email=…&code=…`. Submits the code
 * on arrival, then asks for the password once so the person is signed in
 * and sent to onboarding — the link is the only click the email needs.
 */
export function ConfirmScreen() {
  const [params] = useSearchParams();
  const { status, confirmSignUp, signIn } = useAuth();
  const email = params.get("email") ?? "";
  const code = params.get("code") ?? "";
  const [state, setState] = useState<"confirming" | "confirmed" | "failed">(
    "confirming",
  );
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!email || !code) {
      setState("failed");
      setError(
        "This link is missing its code. Open the email again, or request a new link from sign-in.",
      );
      return;
    }
    confirmSignUp(email, code)
      .then(() => setState("confirmed"))
      .catch((err: unknown) => {
        const msg = messageOf(err, "Could not confirm.");
        // Already confirmed is a success from the person's point of view.
        if (/already|CONFIRMED/i.test(msg)) setState("confirmed");
        else {
          setState("failed");
          setError(msg);
        }
      });
  }, [email, code, confirmSignUp]);

  if (status === "signed_in") return <Navigate to="/" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
    } catch (err) {
      setError(messageOf(err, "Could not sign in."));
    } finally {
      setBusy(false);
    }
  };

  if (state === "confirming")
    return <AuthFrame title="Confirming your email…" />;
  if (state === "failed") {
    return (
      <AuthFrame title="That link did not work">
        <ErrorNote message={error} />
        <p className="mt-4 text-[13px] text-muted">
          <Link to="/sign-in" className="text-accent">
            Go to sign-in
          </Link>{" "}
          to request a fresh one.
        </p>
      </AuthFrame>
    );
  }
  return (
    <AuthFrame
      title="Email confirmed"
      subtitle="Enter your password once to continue to setup."
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Email">
          <Input type="email" value={email} readOnly />
        </Field>
        <Field label="Password">
          <Input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
          />
        </Field>
        <ErrorNote message={error} />
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? "Signing in…" : "Continue"}
        </Button>
      </form>
    </AuthFrame>
  );
}
